import { Input, Output, EventEmitter, Component } from '@angular/core';
import isEmpty from 'lodash-es/isEmpty';

import { isNullOrUndefined } from '@app/util/util';
import { Airport } from '@app/modules/core/interfaces/airport';
import { FlightType } from '@search-bar/entities/flight-type';
import { PlaceType } from '@shared/base-search-bar/entities/place-type';
import { FlightClass } from '@shared/base-search-bar/entities/flight-class';
import { CalendarField } from '@shared/base-search-bar/entities/calendar.entities';
import { PassengerArrangement } from '@shared/base-search-bar/entities/passenger-arrangement';
import { AutocompletePlaces } from '@search-bar/entities/autocomplete-places';
import { SearchType } from '@search-bar/entities/search-type';
import { ChangeInputPayload } from '@app/modules/shared/base-search-bar/entities/search-input-payload';
import { AutocompletePlace } from '@shared/base-search-bar/entities/autocomplete';
import { SelectAutocompletePlace } from '@search-bar/entities/select-autocomplete-place';
import { AmplitudeEventParams } from '@amplitude/interfaces/amplitude-events';
import { AMPLITUDE_EVENTS } from '@app/modules/core/constants/amplitude.constants';
import { EventProperties } from '@avo/interface/trigger-props';
import { AVO_EVENTS } from '@avo/events/avo.events';
import { DateCalendarField } from '@design-system/components/organisms/date-calendar/entities/date-calendar.types';
import { TripTypes } from '@app/entities/trip-types';
import { Icon, IconFillColor } from '@design-system/components/atoms/icon/entities/icon.types';
import { ButtonIconSize } from '@design-system/components/molecules/button-icon/entities/button-icon.types';
import {
  TypographyFontSize, TypographyColor, TypographyFontFamily
} from '@design-system/components/atoms/typography/entities/typography.types';

@Component({
  selector: 'app-search-bar-abstract',
  template: '',
  standalone: false
})
export class SearchBarAbstractComponent {

  @Input() destination: boolean = false;
  @Input() searchType: SearchType;
  @Input() flightType: FlightType;
  @Input() passengerArrangement: PassengerArrangement[];
  @Input() autoCompletePlaces: AutocompletePlaces;
  @Input() airports: Airport[];
  @Input() departurePlace: AutocompletePlace;
  @Input() departureDate: Date;
  @Input() arrivalPlace: AutocompletePlace;
  @Input() arrivalDate: Date;
  @Input() activeFlightClass: FlightClass;
  @Input() defaultPlace: SelectAutocompletePlace;
  @Input() isLoadingAutocompletePlaces: boolean;
  @Input() exchangeTripType: TripTypes;
  @Input() routeChangeIsDisabled: boolean;
  
  @Output() emitArrangement: EventEmitter<PassengerArrangement> = new EventEmitter<PassengerArrangement>();
  @Output() selectFlightCalendar: EventEmitter<Date[]> = new EventEmitter<Date[]>();
  @Output() emitFlightClassSelection: EventEmitter<FlightClass> = new EventEmitter<FlightClass>();
  @Output() placeInputChanged: EventEmitter<ChangeInputPayload> = new EventEmitter<ChangeInputPayload>();
  @Output() selectPlace: EventEmitter<SelectAutocompletePlace> = new EventEmitter<SelectAutocompletePlace>();
  @Output() selectFlightType: EventEmitter<FlightType> = new EventEmitter<FlightType>();
  @Output() emitSearch: EventEmitter<void> = new EventEmitter<void>();
  @Output() swapLocationsEmitter: EventEmitter<void> = new EventEmitter<void>();
  @Output() removeAutoCompletePlaces: EventEmitter<void> = new EventEmitter<void>();
  @Output() amplitudeEvent: EventEmitter<AmplitudeEventParams> = new EventEmitter<AmplitudeEventParams>();

  public showCalendarInput: boolean = false;
  public activeCalendarType: CalendarField = CalendarField.departure;
  public departure: PlaceType = PlaceType.DEPARTURE;
  public arrival: PlaceType = PlaceType.ARRIVAL;
  public flightTypes: FlightType[] = [
    FlightType.ROUNDTRIP,
    FlightType.ONEWAY
  ];

  public dateCalendarField: DateCalendarField = 'left';
  
  public isSubmitted: boolean = false;

  public readonly icons = Icon;
  public readonly iconFillColor = IconFillColor;

  public readonly buttonIconSize = ButtonIconSize.LG;

  public readonly fontSize = TypographyFontSize;
  public readonly fontColor = TypographyColor;
  public readonly fontFamily = TypographyFontFamily;

  get isOneWayTrip(): boolean {
    return this.flightType === FlightType.ONEWAY;
  }

  get activeFlightClassText(): FlightClass {
    return isNullOrUndefined(this.activeFlightClass) ? FlightClass.CHEAPER : this.activeFlightClass;
  }

  get isBuses(): boolean {
    return this.searchType === SearchType.BUSES_RESULTS || this.searchType === SearchType.HOME_BUSES;
  }

  get calendarField(): typeof CalendarField {
    return CalendarField;
  }

  get samePlaceError(): boolean {
    return !isEmpty(this.arrivalPlace?.code) && !isEmpty(this.departurePlace?.code)
      ? this.arrivalPlace?.code === this.departurePlace.code : false;
  }

  get isEmptyArrivalPlace(): boolean {
    return this.isEmptyPlace(this.arrivalPlace, this.isSubmitted);
  }

  get isEmptyDeparturePlace(): boolean {
    return this.isEmptyPlace(this.departurePlace, this.isSubmitted);
  }

  get disabledButton(): boolean {
    return (this.isEmptyArrivalPlace || this.isEmptyDeparturePlace ||
      this.invalidArrivalPlace(this.arrivalPlace,this.departurePlace)) ||
      this.areInvalidDates(this.departureDate, this.arrivalDate);
  }

  get isValidArrivalPlace(): boolean {
    return this.samePlaceError || this.isEmptyArrivalPlace;
  }

  get showArrivalInput(): boolean {
    return this.exchangeTripType ?
      ((this.isOneWayTrip && this.exchangeTripType === TripTypes.RETURN) || !this.isOneWayTrip) : !this.isOneWayTrip;
  }

  get showDepartureInput(): boolean {
    return this.exchangeTripType ? this.exchangeTripType === TripTypes.DEPARTURE : true;
  }

  public setCalendarField(type: DateCalendarField): void {
    this.showCalendarInput = true;
    this.dateCalendarField = type;
  }

  public selectCalendarInput(type: CalendarField): void {
    this.showCalendarInput = true;
    this.activeCalendarType = type;
  }

  public hideCalendarInput(): void {
    this.showCalendarInput = false;
  }

  public applyCalendar(event: Date[]): void {
    this.selectFlightCalendar.emit(event);
    this.hideCalendarInput();
  }

  public isActiveFlightType(type: string): boolean {
    return type === this.flightType;
  }

  public locationNotFoundEvent(props: EventProperties): void {
    this.amplitudeEvent.emit({ type: AVO_EVENTS.TRAVEL_LOCATION_NOT_FOUND, props });
  }

  public popularSelectedEvent(props: EventProperties): void {
    this.amplitudeEvent.emit({ type: AMPLITUDE_EVENTS.SELECT_TRAVEL_POPULAR_DESTINATIONS, props });
  }

  public invalidArrivalPlace(arrivalPlace: AutocompletePlace, departurePlace: AutocompletePlace): boolean {
    return isEmpty(arrivalPlace)  || (arrivalPlace?.code === departurePlace?.code);
  }

  public isEmptyPlace(place: AutocompletePlace, isSubmitted: boolean): boolean {
    return !!place && isNullOrUndefined(place.code) && isSubmitted;
  }

  private areInvalidDates(departureDate: Date, arrivalDate: Date): boolean {
    return (this.showDepartureInput && isNullOrUndefined(departureDate)) || 
      (this.showArrivalInput && isNullOrUndefined(arrivalDate));
  }

}
